import { apolloClient, UPDATE_USER_QUIZZES_MUTATION, UPDATE_USER_QUIZ_MUTATION } from '~/common/apollo'
import { GET_SELECT_OPTIONS, GET_USER_FORM } from '~/common/apollo/queries/user.query'
import { PATHS } from '~/common/constants'
import { action, computed, loading, observable, persist, runInAction, store } from '~/common/mobx.decorator'
import { homeStore } from '~/features/home/home.store'
import { useFormatValues, getDestroyQuestions } from '~/features/profile-completion/utils/hooks/useFormHelper'
import { areAllFieldsCompleted, getQuizObject, returnQuizState } from '~/features/profile-completion/utils/quiz.helper'
import { authStore, notifyStore, routerStore } from '~/stores'
import candidateStore from '~/stores/candidate.store'
import { QUIZ_STATES } from './utils/constants'
import { isEmpty } from 'lodash'
import { PROFILE_STEP_SUBTITLE } from '~/common/constants/common.constant'

const PERCENTAGE_STEP = 20 // 20% per step

/**
 * Note: have no idea why we need the name but this is required field
 */
const NAME = 'initial_screening'

/**
 * Represents the store for managing profile completion data.
 */
@store({ persist: true, name: 'profile-completion' })
class ProfileCompletionStore {
	/** The translation code */
	@observable pageTitle = 'COMPLETE_YOUR_APPLICATION'

	/**
	 * The completion percentage of the profile.
	 * @type {number}
	 */
	@observable percentage = 0

	/**
	 * Contains all parameters used for submitting the profile completion.
	 * Base parameters are:
	 * - kind: value of PROFILE_KINDS
	 * - percent: the completion percentage
	 * @type {Object}
	 */
	@persist @observable params = {
		kind: null,
		percent: 0,
	}

	/**
	 * The status of the profile completion.
	 * @type {null}
	 */
	@persist @observable status = null

	/**
	 * The previous step of the profile completion.
	 * @type {null}
	 */
	@observable previousStep = null

	/**
	 * The state of the profile completion.
	 * @type {null}
	 */
	@observable state = null

	/**
	 * The quiz of the profile completion.
	 * @type {null}
	 */
	@observable quiz = null

	/**
	 * The user's quiz answers.
	 * @type {Array}
	 */
	@observable userQuizAnswers = []

	/**
	 * The ID of the profile completion.
	 * @type {null}
	 */
	@observable id = null

	@observable userQuizzes = []

	@observable currentQuizQuestions = []
	@observable currentQuizAnswers = []

	@observable currentQuizId = null
	@observable currentUserQuiz = null

	@observable percentageStep = {}

	@computed
	get hasNextPage() {
		return this.selectOptions?.pageInfo?.hasNextPage || false
	}

	@computed
	get endCursor() {
		return this.selectOptions?.pageInfo?.endCursor
	}

	/**
	 * The quiz history.
	 *
	 * @type {Object}
	 * @property {Object} previous - The previous quiz's name.
	 * @property {Object} next - The next quiz's name.
	 * @property {Object} current - The current quiz's name.
	 * @property {Object} latest - The latest quiz's name that the user must be completed
	 */
	@observable quizHistory = {
		previous: null,
		current: null,
		next: null,
		latest: null,
	}

	/**
	 * @param {string} key - The key is the value of `PROFILE_COMPLETION_FIELDS_NAME`
	 * @param {Array | Object} value - The value of the answer
	 */
	@observable selectOptionsByDataType = {}

	/**
	 * Accept term and policy
	 * @deprecated will remove soon
	 * */
	@observable acceptTermAndPolicy = false

	/**
	 * Condition to display the checkbox term and policy
	 * @deprecated will remove soon
	 *  */
	@computed
	get isDisplayTermAndPolicy() {
		// TODO: Logic to this play the term and policy
		// if the quiz is the first quiz, we need to display the term and policy
		return !this.currentUserQuiz?.previousStep || this.currentUserQuiz?.previousStep === 'switch_general_information_quiz'
	}

	@computed
	get currentQuizName() {
		return this.currentUserQuiz?.quiz?.name || ''
	}

	@computed
	get isLastQuiz() {
		if (!this.userQuizzes?.length) return false

		// Get last quiz from userQuizzes array
		const lastQuiz = this.userQuizzes[this.userQuizzes.length - 1]

		// Check if current quiz matches last quiz by id
		return (
			this.currentQuizId === lastQuiz.id ||
			// Or check by quiz name
			this.currentQuizName === lastQuiz.quiz?.name
		)
	}

	// Return the total questions of the current quiz to calculate the form complete or further
	@computed
	get currentQuizTotalQuestions() {
		return this.currentUserQuiz?.quiz?.quizQuestions?.length || 0
	}

	@computed
	get totalPercentageCompleted() {
		if (!this.currentUserQuiz?.quiz?.stepPercentage) return 0
		return this.currentUserQuiz.quiz?.stepPercentage
	}

	@computed
	get isCompleted() {
		return this.currentUserQuiz?.quiz?.isCompletedStep || false
	}

	@computed
	get isFirstStep() {
		return isEmpty(this.currentUserQuiz?.previousStep)
	}

	@computed
	get subTitle() {
		if (this.isFirstStep) {
			return 'BACKGROUND'
		}

		const subTitle = PROFILE_STEP_SUBTITLE[this.currentUserQuiz?.quiz?.name]
		if (subTitle) {
			return subTitle
		}

		return null
	}

	@action setQuizId = (quizId) => {
		this.currentQuizId = quizId
	}

	@computed
	get unansweredQuestions() {
		const quizQuestions = this.currentUserQuiz?.quiz?.quizQuestions || []
		const userQuizAnswers = this.currentQuizAnswers || []

		// Create a set of answered question IDs for faster lookup
		const answeredQuestionIds = new Set(userQuizAnswers.filter((x) => !!x.answer).map((answer) => answer.quizQuestionId))

		// Filter out questions that don't have answers
		return quizQuestions.filter((question) => !answeredQuestionIds.has(question.id))
	}

	@action setCurrentUserQuiz = (userQuiz) => {
		this.quizHistory = {
			latest: userQuiz?.quiz?.name,
			previous: userQuiz?.previousStep,
			current: userQuiz?.quiz?.name,
			next: null,
		}
		this.setQuizId(userQuiz?.quiz?.id) // Use quiz.id for API calls
		this.currentUserQuiz = userQuiz
	}

	@action
	addResumeParseAnswer = (answers) => {
		this.currentQuizAnswers = [...this.currentQuizAnswers, ...answers]
	}

	/**
	 *
	 * @param {*} newQuiz - previous quiz
	 * @param {*} currentQuiz - the current quiz
	 */
	@action handleBackQuiz = (newQuiz, currentQuiz) => {
		this.quizHistory = {
			...this.quizHistory,
			previous: newQuiz?.previousStep,
			current: newQuiz?.quiz?.name,
			next: currentQuiz?.quiz?.name,
		}
		this.setQuizId(newQuiz?.quiz?.id) // Use quiz.id for API calls
		this.currentUserQuiz = newQuiz
	}

	/**
	 *
	 * @param {*} newQuiz - can next quiz
	 * @param {*} currentQuiz - the current quiz
	 */
	@action handleNextQuiz = (newQuiz, currentQuiz) => {
		// Get the next quiz name via find the quiz has previousStep is the current quiz name
		const nextQuiz = this.userQuizzes.find(({ previousStep }) => previousStep === newQuiz?.quiz?.name)

		this.quizHistory = {
			...this.quizHistory,
			current: newQuiz?.quiz?.name,
			previous: currentQuiz?.quiz?.name,
			next: newQuiz?.quiz?.name === this.quizHistory.latest ? null : nextQuiz?.quiz?.name,
		}
		this.setQuizId(newQuiz?.quiz?.id) // Use quiz.id for API calls
		this.currentUserQuiz = newQuiz
	}

	/**
	 * @deprecated will remove soon
	 */
	@action setAcceptTermAndPolicy = (value) => {
		this.acceptTermAndPolicy = value
	}

	@action
	setOptionsViaDataType = (dataType, options) => {
		this.selectOptionsByDataType[dataType] = options
	}

	@action
	getOptionsViaDataType = (dataType) => {
		return this.selectOptionsByDataType[dataType] || {}
	}

	/**
	 * @param {PROFILE_COMPLETION_FIELDS_NAME} dataType
	 */
	@action
	getOptionsDataViaDataType = (dataType) => {
		if (!this.selectOptionsByDataType) return []
		const { data } = this.selectOptionsByDataType[dataType] || []

		return data
	}

	@action
	setPageTitle = (title) => {
		this.pageTitle = title
	}

	@action
	setCurrentQuizQuestions = (questions) => {
		const { quiz, userQuizAnswers } = questions || {}
		this.currentQuizQuestions = quiz?.quizQuestions
		this.setCurrentQuizAnswers(userQuizAnswers)
	}

	@action
	setCurrentQuizAnswers = (answers) => {
		if (Array.isArray(answers) && answers.length <= 0) return
		this.currentQuizAnswers = answers.filter(({ _destroy }) => !_destroy)
	}

	@action
	updateCurrentQuizAnswers = (answers) => {
		//TODO: Just update the answer if it has changed
		this.currentQuizAnswers = answers
	}

	/**
	 * Set the status of the profile completion.
	 * @param {any} status - The status to set.
	 */
	@action
	setStatus(status) {
		this.status = status
	}

	/**
	 * Set the user quizzes.
	 * @param {Object} quizzes - The quizzes to set.
	 */
	@action
	setUserQuizzes = (quizzes) => {
		// TODO: add more logic
		this.userQuizzes = quizzes
	}

	/**
	 * Update the parameters of the profile completion.
	 * @param {Object} params - The parameters to update.
	 */
	@action
	updateParams = (params) => {
		this.params = {
			...this.params,
			...params,
		}
	}

	/**
	 * Get the parameters as a query string.
	 * @returns {string} The parameters as a query string.
	 */
	@action
	getParamsAsQuery = () => {
		return Object.keys(this.params)
			.map((key) => `${key}=${this.params[key]}`)
			.join('&')
	}

	/**
	 * Increase the completion percentage.
	 *
	 * @deprecated will remove soon
	 */
	@action
	increasePercentage = () => {
		this.percentage += PERCENTAGE_STEP
	}

	@action
	updatePercentage = (value) => {
		this.percentage = value
	}

	/**
	 * Decrease the completion percentage.
	 */
	@action
	decreasePercentage = () => {
		this.percentage -= PERCENTAGE_STEP
	}

	/**
	 * Reset the completion percentage to 0.
	 */
	@action
	resetPercentage = () => {
		this.percentage = 0
	}

	@action
	reset = () => {
		runInAction(() => {
			this.percentage = 0
			this.params = {
				kind: null,
				percent: 0,
			}
			this.status = null
			this.previousStep = null
			this.state = null
			this.quiz = null
			this.userQuizAnswers = []
			this.id = null

			this.userQuizzes = []
			this.currentQuizQuestions = []
			this.currentQuizAnswers = []
			this.currentQuizId = null
			this.currentUserQuiz = null
			this.selectOptionsByDataType = {}
			this.acceptTermAndPolicy = false
			this.pageTitle = ''
			this.percentageStep = {}

			authStore.setRequireCompleteProfile(false)
		})
	}

	@action
	handleUserCompletedProfile = async (status) => {
		// Check if the user has completed the profile
		if (!status) {
			return
		}
		// 	Fetch the worker profile
		candidateStore.fetchCandidateDetail()
		// 	Set the `requireCompleteProfile` to `false`
		authStore.setRequireCompleteProfile(false)
		// 	Direct to the home page
		routerStore.replace(PATHS.care.home)
		// 	Show the dialog to notice the user that the profile completion is completed
		homeStore.updateUserJustCompletedProfile(true)
		return
	}

	/**
	 * Fetch the user data form to render the initial screening form.
	 * @returns {Array} The user quizzes.
	 */
	@action
	fetchUserForm = async () => {
		try {
			const res = await apolloClient.query({
				query: GET_USER_FORM,
				variables: {
					name: NAME,
				},
			})

			if (!res) {
				throw new Error('No response')
			}

			const { userForm } = res.data || {}
			const { userQuizzes, status } = userForm || {}
			await this.handleUserCompletedProfile(status, userQuizzes)
			const currentQuiz = getQuizObject(userQuizzes)

			userQuizzes.forEach((userQuiz) => {
				this.percentageStep[userQuiz?.quiz?.name] = userQuiz?.state === QUIZ_STATES.ANSWERED ? 100 : 0
			})

			this.setUserQuizzes(userQuizzes)
			this.setCurrentUserQuiz(currentQuiz)
			return userQuizzes
		} catch (e) {
			notifyStore.error(e?.message || "Couldn't fetch user form")
			console.error(e)
		}
	}

	/**
	 * @param {*} dataType - The question options data type to fetch the list of options
	 * @param {*} payload - The payload to fetch the list of options
	 *
	 * @returns {Array} The list of answers
	 */
	@action
	fetchQuestionOptions = async (dataType, payload = {}, loadMore = false) => {
		try {
			this.loadingOptions = true

			// Get current data for loadMore case
			const currentData = this.getOptionsViaDataType(dataType)
			const cursor = loadMore ? currentData?.rest?.pageInfo?.endCursor : null

			const res = await apolloClient.query({
				query: GET_SELECT_OPTIONS,
				variables: {
					filter: {
						dataType,
						payload,
					},
					first: 40,
					after: cursor, // Use cursor for pagination
				},
			})

			const { selectOptions } = res.data || {}
			if (!selectOptions) {
				console.error('No select options')
				return
			}

			const { nodes, pageInfo } = selectOptions || {}
			const remappedOptions = nodes.map(({ id, name }) => ({
				value: id,
				label: name,
			}))

			// Update data based on loadMore flag
			if (loadMore && currentData?.data) {
				this.setOptionsViaDataType(dataType, {
					data: [...currentData.data, ...remappedOptions],
					rest: { pageInfo }, // Store new pageInfo
				})
			} else {
				this.setOptionsViaDataType(dataType, {
					data: remappedOptions,
					rest: { pageInfo },
				})
			}

			return selectOptions
		} catch (e) {
			notifyStore.error(e?.message || "Couldn't fetch question options")
			console.error(e)
		} finally {
			this.loadingOptions = false
		}
	}

	/**
	 * The function responsible for submitting the profile completion step to the server.
	 *
	 * @param {Array} values - The values of the form. [UserQuizAnswerInput!]
	 * @param {boolean} isFormCompleted
	 */
	@action
	submitUpdateUserQuiz = async (values) => {
		try {
			const quizId = this.currentQuizId
			if (!quizId) {
				throw new Error('No quiz ID')
			}

			// Get question IDs for current quiz only
			const currentQuizQuestionIds = new Set(this.currentQuizQuestions?.map((q) => q.id) || [])

			// Filter values to only include answers for current quiz questions
			const currentQuizValues = values.filter(({ answer }) => currentQuizQuestionIds.has(answer?.quizQuestionId))

			const answers = useFormatValues(currentQuizValues)
			const destroyQuestions = getDestroyQuestions(currentQuizValues)

			const userQuizAnswers = [...answers, ...destroyQuestions]

			const isFormCompleted = areAllFieldsCompleted(answers, this.currentQuizTotalQuestions)

			// 2nd gate to check the form is completed or not
			// Maybe no need after the disabled on the UI
			if (!isFormCompleted) {
				notifyStore.error('Please ensure all questions are answered before submitting.')
				return
			}

			const nextState = returnQuizState(isFormCompleted)
			const variables = {
				quizId,
				state: nextState,
				userQuizAnswers,
			}

			const res = await apolloClient.mutate({
				mutation: UPDATE_USER_QUIZZES_MUTATION,
				variables: variables,
			})

			this.percentageStep[this.currentQuizName] = 100

			const { updateUserQuiz } = res.data || {}
			if (!updateUserQuiz) {
				throw new Error('No response') // TODO: change the error message to a better one
			}

			const { status, userQuizzes } = updateUserQuiz || {}
			await this.handleUserCompletedProfile(status, userQuizzes)
			const currentQuiz = getQuizObject(userQuizzes)

			this.setUserQuizzes(userQuizzes)
			this.setCurrentUserQuiz(currentQuiz)

			this.fetchUserForm()
		} catch (e) {
			void notifyStore.error(e?.message || "Couldn't submit profile completion")
			console.error(e)
		}
	}

	/**
	 * This function take responsible for find the previous/next stage in the `userForm` based on the `stateName`
	 * @param {*} quizName
	 */
	@action
	switchStage = (quizName, isBack = true) => {
		try {
			const { userQuizzes } = this
			const newQuiz = userQuizzes.find(({ quiz }, idx) => {
				if (quiz.name === quizName) {
					return userQuizzes[idx]
				}

				return null
			})

			if (!newQuiz) {
				throw new Error(`Can't find quiz with name: ${quizName}`)
			}
			if (isBack) {
				this.handleBackQuiz(newQuiz, this.currentUserQuiz)
			} else {
				// TODO: handle nextquiz here
				this.handleNextQuiz(newQuiz, this.currentUserQuiz)
			}
		} catch (e) {
			notifyStore.error(e?.message || "Couldn't switch stage")
		}
	}

	/**
	 * This function used to submit the answer on each change of the form - Auto Save
	 * */
	@loading
	@action
	submitSingleAnswer = async (answer) => {
		console.log('🚀 ~ ProfileCompletionStore ~ answer:', JSON.stringify(answer, null, 2))
		try {
			const quizId = this.currentQuizId

			if (!quizId) {
				throw new Error('No quiz ID')
			}

			const existingAnswer = this.currentQuizAnswers.find((a) => a.quizQuestionId === answer.quizQuestionId)

			const answerWithId = {
				...answer,
				id: existingAnswer?.id,
			}

			const answers = useFormatValues([{ answer: answerWithId }], this.currentQuizAnswers)

			if (answers?.length <= 0) return Promise.resolve()

			const userQuizAnswers =
				answers?.map((item) => {
					if (item?.answer === 'AVOID_ANSWER_BLANK') {
						return {
							...item,
							answer: '',
						}
					}
					return item
				}) || []

			// Latest step is remap answer has value `AVOID_ANSWER_BLANK` to empty string
			// to avoid the issue related to BE
			const variables = {
				quizId,
				state: QUIZ_STATES.ANSWERING,
				userQuizAnswers,
			}
			const res = await apolloClient.mutate({
				mutation: UPDATE_USER_QUIZ_MUTATION,
				variables: variables,
			})
			const { updateUserQuiz } = res.data || {}

			if (!updateUserQuiz) {
				throw new Error("Can't save") // TODO: change the error message to a better one
			}

			// Optimistic update: chỉ update answer đã submit, không replace toàn bộ
			const { userQuizzes } = updateUserQuiz || {}
			const currentQuiz = Array.from(userQuizzes).find((userQuiz) => userQuiz?.quiz?.id === quizId) || {}
			const updatedAnswers = currentQuiz?.userQuizAnswers || []

			// Nếu là delete (_destroy: true), remove answer khỏi local state
			if (answer._destroy) {
				this.currentQuizAnswers = this.currentQuizAnswers.filter((a) => a.quizQuestionId !== answer.quizQuestionId)
			} else {
				// Chỉ update ID từ API response để mapping, không replace toàn bộ answer
				// Điều này tránh việc Formik reinitialize và gây flip UI
				const submittedAnswer = updatedAnswers.find((a) => a.quizQuestionId === answer.quizQuestionId)
				if (submittedAnswer) {
					const index = this.currentQuizAnswers.findIndex((a) => a.quizQuestionId === answer.quizQuestionId)
					if (index !== -1) {
						// Chỉ update id, giữ nguyên các giá trị khác từ local state
						// Đối với file upload, cần merge files từ API response
						const updatedAnswer = {
							...this.currentQuizAnswers[index],
							id: submittedAnswer.id,
						}
						// Nếu có files từ API response, merge vào để có đầy đủ thông tin (filename, byteSize, fileUrl)
						if (submittedAnswer.files && submittedAnswer.files.length > 0) {
							updatedAnswer.files = submittedAnswer.files
						}
						this.currentQuizAnswers[index] = updatedAnswer
					} else {
						// Nếu chưa có trong local, thêm mới với data từ API
						// Đối với file upload, sử dụng files từ API response
						const newAnswer = {
							...answer,
							id: submittedAnswer.id,
						}
						if (submittedAnswer.files && submittedAnswer.files.length > 0) {
							newAnswer.files = submittedAnswer.files
						}
						this.currentQuizAnswers.push(newAnswer)
					}
				}
			}
		} catch (e) {
			void notifyStore.error(e?.message || 'Error')
			// Re-fetch user form
			this.fetchUserForm()
		}
	}

	@action
	removeAnswerByQuestionId = (questionId) => {
		this.currentQuizAnswers = this.currentQuizAnswers.filter((answer) => answer?.quizQuestionId !== questionId)
	}

	@action
	clearOptionsForDataType = (dataType) => {
		if (this.selectOptionsByDataType[dataType]) {
			this.selectOptionsByDataType[dataType] = null
		}
	}
}

export const profileCompletionStore = new ProfileCompletionStore()
